/* eslint-disable */
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from '@/hooks/use-toast';

const TIMEOUT = 30000;

const ERROR_MESSAGES = {
  400: {
    title: 'Demande invalide',
    description:
      'Les informations saisies ne sont pas valides. Veuillez vérifier et réessayer.',
  },
  401: {
    title: 'Accès non autorisé',
    description: 'Votre session a expiré. Veuillez vous reconnecter.',
  },
  403: {
    title: 'Accès interdit',
    description: "Vous n'avez pas l'autorisation d'accéder à cette ressource.",
  },
  404: {
    title: 'Page introuvable',
    description: "La page ou l'information que vous cherchez n'existe pas.",
  },
  408: {
    title: "Délai d'attente dépassé",
    description: 'La demande a pris trop de temps. Veuillez réessayer.',
  },
  422: {
    title: 'Données incorrectes',
    description: 'Certaines informations sont manquantes ou incorrectes.',
  },
  429: {
    title: 'Trop de tentatives',
    description:
      'Vous avez fait trop de tentatives. Veuillez patienter quelques minutes.',
  },
  500: {
    title: 'Erreur du serveur',
    description:
      'Un problème technique est survenu. Nous travaillons à le résoudre.',
  },
  502: {
    title: 'Service temporairement indisponible',
    description:
      'Le service est momentanément inaccessible. Veuillez réessayer dans quelques minutes.',
  },
  503: {
    title: 'Maintenance en cours',
    description: 'Le service est en maintenance. Veuillez réessayer plus tard.',
  },
  network: {
    title: 'Problème de connexion',
    description: 'Vérifiez votre connexion internet et réessayez.',
  },
  timeout: {
    title: "Délai d'attente dépassé",
    description:
      'La demande a pris trop de temps. Vérifiez votre connexion et réessayez.',
  },
  default: {
    title: 'Une erreur est survenue',
    description: "Quelque chose s'est mal passé. Veuillez réessayer.",
  },
};

const getCsrfToken = () => Cookies.get('csrftoken');

/**
 * Instance Axios configurée
 * @type {axios.AxiosInstance}
 */
const axiosInstance = axios.create({
  baseURL: '',
  headers: {
    'Content-Type': 'application/json',
    'Accept-Language': '*',
  },
  // timeout: TIMEOUT,
  withCredentials: true,
});

/**
 * Intercepteur de requête
 */
axiosInstance.interceptors.request.use(
  async (config) => {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken;
    }

    if (config.method === 'get' && config.params) {
      config.params._t = Date.now();
    }

    return config;
  },
  (error) => {
    console.error('Erreur de configuration de la requête:', error);
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (!error.response) {
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        toast({
          variant: 'destructive',
          title: ERROR_MESSAGES.timeout.title,
          description: ERROR_MESSAGES.timeout.description,
        });
      } else {
        toast({
          variant: 'destructive',
          title: ERROR_MESSAGES.network.title,
          description: ERROR_MESSAGES.network.description,
        });
      }
      return Promise.reject(error);
    }

    const { response } = error;
    const status = response.status;

    if (status === 401) {
      toast({
        variant: 'destructive',
        title: ERROR_MESSAGES[401].title,
        description: ERROR_MESSAGES[401].description,
      });

      return Promise.reject(error);
    }

    let errorMessage = ERROR_MESSAGES[status] || ERROR_MESSAGES.default;

    if (response.data && response.data.message) {
      errorMessage = {
        title: errorMessage.title,
        description: response.data.message,
      };
    } else if (response.data && response.data.error) {
      errorMessage = {
        title: errorMessage.title,
        description: response.data.error,
      };
    }

    toast({
      variant: 'destructive',
      title: errorMessage.title,
      description: errorMessage.description,
    });
    return Promise.reject(error);
  }
);

/**
 * Fonction utilitaire pour les requêtes avec retry automatique
 */
export const apiRequest = async (config, maxRetries = 2) => {
  let lastError;

  for (let i = 0; i <= maxRetries; i++) {
    try {
      return await axiosInstance(config);
    } catch (error) {
      lastError = error;

      if (
        error.response &&
        [400, 401, 403, 404, 422].includes(error.response.status)
      ) {
        break;
      }

      if (i < maxRetries) {
        await new Promise((resolve) =>
          setTimeout(resolve, Math.pow(2, i) * 1000)
        );
      }
    }
  }

  throw lastError;
};

/**
 * API wrapper for http requests
 * @type {{patch: (function(*, *, {}=): Promise<axios.AxiosResponse<any>|undefined>), post: (function(*, *, {}=): Promise<axios.AxiosResponse<any>|undefined>), get: (function(*, {}=): Promise<axios.AxiosResponse<any>|undefined>), direct: axios.AxiosInstance, delete: (function(*, {}=): Promise<axios.AxiosResponse<any>|undefined>), put: (function(*, *, {}=): Promise<axios.AxiosResponse<any>|undefined>)}}
 */
export const api = {
  get: (url, config = {}) => apiRequest({ method: 'get', url, ...config }),
  post: (url, data, config = {}) =>
    apiRequest({ method: 'post', url, data, ...config }),
  put: (url, data, config = {}) =>
    apiRequest({ method: 'put', url, data, ...config }),
  patch: (url, data, config = {}) =>
    apiRequest({ method: 'patch', url, data, ...config }),
  delete: (url, config = {}) =>
    apiRequest({ method: 'delete', url, ...config }),
  direct: axiosInstance,
};

export default axiosInstance;
